import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/forms/[handler]'
import { hasNoLetters } from '../../const/letterHelper'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import style from '../layout/Footer.module.sass'
import { MessageInfoSchema } from '../shared/MessageInfoSchema'

type NullableValues<R extends Record<string, unknown>> = {
	[K in keyof R]: R[K] | null
}

const api = createFetcher<API>('/api/forms/[handler]')

type NullableMessageInfoSchema = NullableValues<MessageInfoSchema>

export const FooterMessageForm = React.memo(function FooterMessageForm() {
	const [messageAlertVisible, setMessageAlertVisible] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const [email, setEmail] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isMessageSent, setIsMessageSent] = useState(false)
	const [emailAlertVisible, setEmailAlertVisible] = useState(false)

	const router = useRouter()

	const schema = useMemo<NullableMessageInfoSchema>(() => {
		return {
			message,
			email,
		}
	}, [email, message])

	const onChange = useCallback((change: NullableMessageInfoSchema) => {
		console.log({ change })
	}, [])

	useEffect(() => {
		console.log('onChange', schema)
		onChange?.(schema)
	}, [schema, onChange])

	const messageInfo = useMemo(() => {
		return MessageInfoSchema.safeParse({ ...schema })
	}, [schema])

	const clearForm = useCallback(() => {
		setMessage('')
		setEmail('')
	}, [])

	const submitMessage = async (e: SyntheticEvent) => {
		e.preventDefault()

		if (messageInfo.success && !hasNoLetters(message)) {
			setIsLoading(true)
			try {
				await api('createContactFormMessage', {
					email: String(email),
					message: String(message),
					url: router.asPath,
				})
				await api('sendMailReport', {
					email: String(email),
					message: String(message),
					url: router.asPath,
				})
				setIsMessageSent(true)
				setIsLoading(false)
				clearForm()
			} catch (err) {
				console.log('error', err)
			}
			return
		}
	}

	const checkValidity = useCallback(() => {
		if ((message && message.length < 10) || hasNoLetters(message)) {
			setMessageAlertVisible(true)
		}
	}, [message])

	const translate = useTranslate()

	return (
		<form action="" method="POST" onSubmit={submitMessage}>
			<fieldset>
				<div className={style.messageForm}>
					<div className={style.textareaWrapper}>
						<textarea
							className={style.textarea}
							rows={4}
							value={message ?? ''}
							placeholder={translate('footer.column.first.textarea.placeholder')}
							onChange={(e) => setMessage(e?.target.value)}
							required
							onFocus={() => setMessageAlertVisible(false)}
						/>
						{messageAlertVisible && (
							<span className={style.textareaAlert}>
								{hasNoLetters(message) ? (
									<Translation tr="message.punctuation.alert" />
								) : (
									<Translation tr="footer.column.first.textarea.alert" />
								)}
							</span>
						)}
					</div>

					<div className={clsx(style.inputWrapper, style.inputWrapperMargin)}>
						<input
							className={style.input}
							placeholder={translate('email.placeholder')}
							name="email"
							type="email"
							required
							value={email ?? ''}
							onFocus={() => setEmailAlertVisible(false)}
							onChange={(e) => setEmail(e?.target.value)}
						/>
						{emailAlertVisible && (
							<span className={style.inputAlert}>
								<Translation tr="footer.column.first.email.alert" />
							</span>
						)}
					</div>
					{isMessageSent ? (
						<div className={style.confirmationMessage}>
							<Translation tr="footer.column.first.confirmationMessage" />
						</div>
					) : (
						<button
							type="button"
							className={clsx(style.formButton, isLoading && style.formButtonNoUnderline)}
							onClick={messageInfo.success ? submitMessage : checkValidity}
						>
							<Translation tr="footer.sendMessage" />
							{isLoading && <div className={style.loader} />}
						</button>
					)}
				</div>
			</fieldset>
		</form>
	)
})
